import React, { useEffect, useState } from 'react';
import { Play, Calendar } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import VideoDemoModal from '../engagement/components/VideoDemoModal';

const DEMO_VIDEO_SRC = '/kolanut.mp4';
const CALENDLY_URL = 'https://www.calendly.com/omale';

const BookDemoPage = () => {
  const [demoOpen, setDemoOpen] = useState(false);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Header />

      <main className="flex-grow pb-24 sm:pb-0">
        <section className="bg-white pt-6 sm:pt-10 md:pt-14 pb-10 sm:pb-16">
          <div className="px-2 sm:px-3 md:px-4">
            <div className="relative isolate overflow-hidden rounded-[1.5rem] sm:rounded-[2rem] lg:rounded-[2.5rem] bg-[#f7f8fa]">
              <div
                className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(138,230,144,0.18),_transparent_55%)]"
                aria-hidden
              />

              <div className="relative z-10 mx-auto max-w-3xl px-5 py-10 sm:px-10 sm:py-14 lg:px-12 lg:py-16 text-center">
                <p className="inline-block rounded-full border border-gray-200 bg-white px-3.5 py-1.5 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.16em] text-gray-600">
                  For early stage B2B SaaS
                </p>

                <h1 className="mt-5 font-serif text-[2.1rem] leading-[1.12] text-gray-900 sm:mt-6 sm:text-4xl md:text-5xl lg:text-[3.25rem] lg:leading-[1.08]">
                  Before you hire another CS seat, put lifecycle on autopilot
                </h1>

                <p className="mt-4 mx-auto max-w-xl text-base leading-relaxed text-gray-500 sm:mt-5 sm:text-lg">
                  Kolanut understands where every user is in your product and runs the right outreach for onboarding, churn saves, and expansion. Let it send on its own, or approve every message first.
                </p>

                <button
                  type="button"
                  onClick={() => setDemoOpen(true)}
                  className="group relative mt-8 sm:mt-10 w-full overflow-hidden rounded-[1.25rem] sm:rounded-[1.5rem] border border-gray-200 bg-stone-900 shadow-[0_12px_40px_rgba(0,0,0,0.12)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8ae690] focus-visible:ring-offset-2"
                  aria-label="Play Kolanut demo video"
                >
                  <div className="relative aspect-video w-full">
                    <img
                      src="/thumbnail.png"
                      alt=""
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                    />
                    <div
                      className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/20 to-black/10"
                      aria-hidden
                    />
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-4">
                      <span className="inline-flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-full bg-[#8ae690] text-black shadow-lg transition-transform duration-200 group-hover:scale-105">
                        <Play className="h-6 w-6 sm:h-7 sm:w-7 fill-current ml-0.5" />
                      </span>
                      <span className="text-sm sm:text-base font-semibold text-white drop-shadow-sm">
                        Watch the demo
                      </span>
                    </div>
                  </div>
                </button>

                <div className="mt-8 sm:mt-10 flex flex-col items-center gap-3">
                  <a
                    href={CALENDLY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex w-full sm:w-auto items-center justify-center gap-2.5 h-12 md:h-14 px-8 md:px-10 rounded-full bg-[#8ae690] text-black font-semibold text-sm md:text-base shadow-md hover:bg-[#7ad67f] hover:shadow-lg transition-all duration-200"
                  >
                    <Calendar className="h-4 w-4" strokeWidth={2.25} aria-hidden />
                    Book a Demo
                  </a>
                  <p className="text-xs sm:text-sm text-gray-400">
                    15 to 30 minutes · See the product · Leave with a clear next step
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <div className="fixed bottom-0 left-0 right-0 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] bg-white/40 backdrop-blur-md border-t border-gray-200/40 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] sm:hidden z-40">
        <a
          href={CALENDLY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full py-3.5 rounded-full bg-[#8ae690] text-black font-bold text-base shadow-lg hover:bg-[#7ad67f] active:scale-[0.98] transition-all duration-200"
        >
          <Calendar className="h-4 w-4" strokeWidth={2.25} aria-hidden />
          Book a Demo
        </a>
      </div>

      <VideoDemoModal
        open={demoOpen}
        onClose={() => setDemoOpen(false)}
        videoSrc={DEMO_VIDEO_SRC}
        title="Watch Kolanut in action"
      />

      <style jsx global>{`
        html {
          scroll-behavior: smooth;
        }
        body {
          overflow-x: hidden;
        }
      `}</style>
    </div>
  );
};

export default BookDemoPage;
